import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useLocation } from 'react-router';

import { Button, type ButtonProps } from '../Button';
import * as DropdownMenu from '../Menu';
import { cn } from '../utils';

const ButtonPropsContext = React.createContext<{
  setActiveButtonProps?: React.Dispatch<React.SetStateAction<ButtonProps | undefined>>;
  activeButtonProps?: ButtonProps;
  setButtonProps?: React.Dispatch<React.SetStateAction<ButtonProps | undefined>>;
  buttonProps?: ButtonProps;
}>({});
const ButtonPropsProvider = (props: { children: React.ReactNode }) => {
  const [buttonProps, setButtonProps] = React.useState<ButtonProps>();
  const [activeButtonProps, setActiveButtonProps] = React.useState<ButtonProps>();
  return (
    <ButtonPropsContext.Provider
      value={{
        setActiveButtonProps,
        activeButtonProps,
        setButtonProps,
        buttonProps,
      }}
    >
      {props.children}
    </ButtonPropsContext.Provider>
  );
};

type NavigationMenuProps = React.ComponentProps<'nav'> & VariantProps<typeof navBarStyle>;

function Menu(props: NavigationMenuProps) {
  const { className, children, variant, ...rest } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const [hiddenIndex, setHiddenIndex] = React.useState<number | undefined>();
  const [hasActive, setHasActive] = React.useState(false);
  const [morePos, setMorePos] = React.useState(0);
  const navClassName = cn(navBarStyle({ variant }), className);
  const location = useLocation();

  React.useEffect(() => {
    if (!ref.current) return;
    const navEl = ref.current;
    const children = navEl.querySelectorAll('[data-nav-item]') as NodeListOf<HTMLElement>;

    const handleVisibility = () => {
      const navRect = navEl.getBoundingClientRect();
      let firstHiddenIndex: undefined | number = undefined;
      let hasActive = false;
      let morePos = 0;

      children.forEach((element, index) => {
        const elRect = element.getBoundingClientRect();
        const elOffset = index === children.length - 1 ? 0 : 70;
        // if the element is outside of the navigations bounds
        if (navRect.right < elRect.right + elOffset) {
          element.style.visibility = 'hidden';
          if (element.getAttribute('data-nav-item-active') === 'true') hasActive = true;
          if (firstHiddenIndex === undefined) {
            morePos = elRect.left - navRect.left;
            firstHiddenIndex = index;
          }
        } else element.style.visibility = 'visible';
      });

      setMorePos(morePos);
      setHasActive(hasActive);
      setHiddenIndex(firstHiddenIndex);
    };
    handleVisibility();

    // observe resize of the ref.current and execute visibility hander every resize
    const resizeObserver = new ResizeObserver(handleVisibility);
    resizeObserver.observe(ref.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [ref, hiddenIndex, setHiddenIndex, location.pathname]);

  return (
    <ButtonPropsProvider>
      <nav ref={ref} className={navClassName} data-nav {...rest}>
        {children}
        <DropDown hiddenIndex={hiddenIndex} leftPos={morePos} dropDownActive={hasActive} {...rest}>
          {children}
        </DropDown>
      </nav>
    </ButtonPropsProvider>
  );
}

const navBarStyle = cva('relative overflow-x-hidden', {
  variants: {
    variant: {
      default: 'flex flex-1 flex-nowrap items-center gap-5',
      unstyled: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type DropDownProps = React.ComponentProps<'nav'> & {
  leftPos: number;
  hiddenIndex?: number;
  dropDownActive?: boolean;
};

const DropDown = (props: DropDownProps) => {
  const { hiddenIndex, leftPos, children, dropDownActive } = props;
  const { buttonProps, activeButtonProps } = React.useContext(ButtonPropsContext);
  if (typeof hiddenIndex !== 'number') return null;

  const visibleChildren = React.Children.map(children, (child, index) =>
    React.isValidElement<NavigationItemProps>(child) && index >= hiddenIndex ? (
      <DropdownMenu.MenuItem className="outline-none">{React.cloneElement(child)}</DropdownMenu.MenuItem>
    ) : null
  );

  return (
    <DropdownMenu.MenuProvider>
      <DropdownMenu.MenuButton
        style={{
          position: 'absolute',
          left: leftPos,
        }}
        render={
          <Button
            iconBefore={hiddenIndex === 0 ? 'ic:round-menu' : 'ic:outline-more-horiz'}
            {...(dropDownActive ? activeButtonProps : buttonProps)}
            asChild={false}
          >
            {hiddenIndex === 0 ? 'Menu' : 'More'}
          </Button>
        }
      />
      <DropdownMenu.Menu>{visibleChildren}</DropdownMenu.Menu>
    </DropdownMenu.MenuProvider>
  );
};

type NavigationItemProps = Omit<ButtonProps, 'variant'> & VariantProps<typeof navItemStyle> & { isActive?: boolean };

const Item = React.forwardRef<HTMLButtonElement, NavigationItemProps>(function Item(props, ref) {
  const { className, variant, isActive, children, disabled, ...rest } = props;
  const { setButtonProps, setActiveButtonProps, buttonProps, activeButtonProps } = React.useContext(ButtonPropsContext);

  React.useEffect(() => {
    const defaultButtonProps: ButtonProps = {
      variant: !variant || variant === 'default' || variant === 'tab' ? 'unstyled' : variant,
      className: cn(navItemStyle({ variant, isActive, disabled }), className),
      disabled: disabled,
      ...(rest as Omit<React.ComponentProps<'button'>, 'ref'>),
    };

    if (!buttonProps) setButtonProps?.(defaultButtonProps);
    if (isActive && !activeButtonProps) setActiveButtonProps?.(defaultButtonProps);
  }, [
    variant,
    isActive,
    disabled,
    className,
    rest,
    buttonProps,
    activeButtonProps,
    setButtonProps,
    setActiveButtonProps,
  ]);

  const buttonPropsToUse = isActive ? activeButtonProps : buttonProps;

  return (
    <Button ref={ref} data-nav-item data-nav-item-active={isActive} {...buttonPropsToUse}>
      {children}
    </Button>
  );
});

const navItemStyle = cva('relative', {
  variants: {
    variant: {
      default: 'border-b-2 p-0',
      plain: 'shadow-card',
      unstyled: '',
      ghost: '',
      outline: '',
      hero: '',
      tab: 'ml-1 rounded-t-md border border-transparent border-b-gray-300 bg-gray-200 hover:border-gray-300 hover:border-b-transparent hover:bg-white',
    },
    isActive: {
      true: '',
    },
    disabled: {
      true: 'cursor-not-allowed',
    },
  },
  defaultVariants: {
    variant: 'default',
    isActive: false,
    disabled: false,
  },
  compoundVariants: [
    { variant: 'default', isActive: false, className: 'border-transparent text-gray-500' },
    { variant: 'default', isActive: true, className: 'border-black' },
    { variant: 'default', disabled: false, className: 'hover:border-black hover:text-black' },
    { variant: 'tab', isActive: true, className: 'border-gray-300 border-b-white bg-white' },
  ],
});

export const Navigation = { Menu, Item };
